// Public Trade Hall Highlights page — shareable landing showcasing top weekly flips.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, Trophy, Flame, ArrowRight, Share2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GEM_COLORS = {
  crown: "#FFD400",
  fire: "#FF6030",
  star: "#F0ABFC",
  prismatic: "#B0F0FF",
};
const GEM_SYMBOL = { crown: "♛", fire: "▲", star: "★", prismatic: "◈" };

function GemChip({ gem }) {
  if (!gem) return null;
  const color = GEM_COLORS[gem] || "#FFF";
  return (
    <span
      className="text-[10px] tracking-[0.3em] px-1.5 py-0.5 border"
      style={{
        borderColor: color,
        color,
        boxShadow: `0 0 6px ${color}55`,
      }}
    >
      {GEM_SYMBOL[gem] || "•"} {gem.toUpperCase()}
    </span>
  );
}

function HandleTag({ handle, gem }) {
  const color = GEM_COLORS[gem];
  return (
    <span
      className={gem === "prismatic" ? "prismatic-text" : ""}
      style={color && gem !== "prismatic" ? { color, textShadow: `0 0 6px ${color}88` } : {}}
    >
      {handle}
    </span>
  );
}

export default function TradeHighlights() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/highlights/trade-hall`)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, []);

  const share = async () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const line = data?.top_trader
      ? `${data.top_trader.handle} leads Parking Plot Escape's Trade Hall this week with ${data.top_trader.chips} chips across ${data.top_trader.sales} sales.`
      : `Parking Plot Escape · Trade Hall Highlights · week of ${data?.week_start || "…"}`;
    const url = `${origin}/trade-highlights`;
    const text = `${line}\n${url}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Trade Hall Highlights", text, url });
        return;
      } catch { /* fall through */ }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("COPIED · share the link anywhere");
    } catch {
      toast.error("Share unavailable");
    }
  };

  return (
    <div className="min-h-screen w-full bg-black text-white grid-bg relative overflow-hidden" data-testid="trade-highlights-page">
      <div className="max-w-5xl mx-auto px-6 py-14 relative z-10">
        {/* Nav */}
        <div className="flex items-center justify-between mb-10">
          <Link
            to="/"
            data-testid="trade-highlights-home-link"
            className="text-[10px] tracking-[0.4em] text-white/60 hover:text-cyan-300 border-b border-transparent hover:border-cyan-300 pb-0.5"
          >
            ← BACK TO GAME
          </Link>
          <button
            data-testid="trade-highlights-share"
            onClick={share}
            className="text-[10px] tracking-[0.4em] px-3 py-1.5 border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC] hover:text-black transition-colors flex items-center gap-2"
          >
            <Share2 size={12} /> SHARE HIGHLIGHTS
          </button>
        </div>

        {/* Hero */}
        <div className="mb-12">
          <div className="text-[10px] tracking-[0.5em] text-[#F0ABFC] mb-3 flex items-center gap-2">
            <Sparkles size={12} /> TRADE HALL · WEEKLY HIGHLIGHTS
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-4">
            Gems change hands.<br />
            <span className="text-[#F0ABFC]">Fortunes change hands.</span>
          </h1>
          <p className="text-white/60 text-sm max-w-xl tracking-wide">
            The Trade Hall's biggest flips of the week — live from the parking lot marketplace.
            Week of <span className="text-white/90 font-mono">{data?.week_start || "…"}</span>.
          </p>
        </div>

        {data && !data.error && (
          <>
            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10" data-testid="trade-highlights-kpis">
              <div className="border border-white/10 p-4 bg-black/40">
                <div className="text-[10px] tracking-[0.4em] text-white/50 mb-2">WEEKLY VOLUME</div>
                <div
                  data-testid="trade-highlights-volume"
                  className="text-3xl font-bold tabular-nums text-[#39FF14]"
                  style={{ textShadow: "0 0 12px rgba(57,255,20,0.4)" }}
                >
                  {data.total_volume}
                </div>
                <div className="text-[10px] text-white/40 mt-1">CHIPS</div>
              </div>
              <div className="border border-white/10 p-4 bg-black/40">
                <div className="text-[10px] tracking-[0.4em] text-white/50 mb-2">TRADES COMPLETED</div>
                <div data-testid="trade-highlights-count" className="text-3xl font-bold tabular-nums text-white">
                  {data.total_trades}
                </div>
                <div className="text-[10px] text-white/40 mt-1">DEALS</div>
              </div>
              {data.top_trader && (
                <div className="border border-[#FFD400]/40 p-4 bg-[#FFD400]/5 md:col-span-1 col-span-2">
                  <div className="text-[10px] tracking-[0.4em] text-[#FFD400] mb-2 flex items-center gap-2">
                    <Trophy size={11} /> TOP TRADER
                  </div>
                  <div data-testid="trade-highlights-top-trader" className="text-lg font-semibold">
                    <HandleTag handle={data.top_trader.handle} gem={data.top_trader.gem} />
                  </div>
                  <div className="text-[10px] text-white/50 mt-1 tabular-nums">
                    {data.top_trader.chips} CHIPS · {data.top_trader.sales} SALES
                  </div>
                </div>
              )}
            </div>

            {/* Top Flips */}
            <div className="mb-10">
              <div className="text-[10px] tracking-[0.5em] text-[#39FF14] mb-4 flex items-center gap-2">
                <Flame size={12} /> TOP FLIPS THIS WEEK
              </div>
              {(!data.top_flips || data.top_flips.length === 0) ? (
                <div className="text-xs text-white/40 border border-white/10 p-6 text-center tracking-[0.3em]">
                  NO TRADES YET · BE THE FIRST TO SPARK THE MARKET
                </div>
              ) : (
                <ol className="space-y-2">
                  {data.top_flips.map((f, i) => (
                    <li
                      key={i}
                      data-testid={`trade-flip-${i}`}
                      className="border border-white/10 bg-black/40 p-4 flex items-center justify-between gap-4 flex-wrap"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-8 text-center text-white/40 tabular-nums">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <GemChip gem={f.gem} />
                        <div className="text-xs text-white/70 min-w-0 truncate">
                          <HandleTag handle={f.seller_handle} gem={f.seller_gem} />
                          <span className="text-white/40 mx-2">→</span>
                          <HandleTag handle={f.buyer_handle} gem={f.buyer_gem} />
                          {f.via_auction && (
                            <span className="ml-2 text-[9px] tracking-[0.3em] text-[#FFD400] border border-[#FFD400]/50 px-1 py-[1px]">
                              AUCTION
                            </span>
                          )}
                        </div>
                      </div>
                      <div
                        className="text-lg font-bold tabular-nums text-[#39FF14]"
                        style={{ textShadow: "0 0 10px rgba(57,255,20,0.4)" }}
                      >
                        {f.price}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            {/* Prismatic Sales */}
            {data.top_prismatic && data.top_prismatic.length > 0 && (
              <div className="mb-10">
                <div className="text-[10px] tracking-[0.5em] mb-4 flex items-center gap-2 prismatic-text">
                  <Sparkles size={12} /> PRISMATIC SALES
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {data.top_prismatic.map((f, i) => (
                    <div
                      key={i}
                      data-testid={`trade-prismatic-${i}`}
                      className="border prismatic-row p-4 bg-black/40 rounded-sm"
                    >
                      <div className="prismatic-text text-xl font-bold tabular-nums mb-2">{f.price}</div>
                      <div className="text-[10px] text-white/50 tracking-wider">
                        <HandleTag handle={f.seller_handle} gem={f.seller_gem} />
                        <span className="text-white/40 mx-1">→</span>
                        <HandleTag handle={f.buyer_handle} gem={f.buyer_gem} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {data?.error && (
          <div className="text-xs text-red-400 tracking-wider">Failed to load highlights.</div>
        )}

        {/* CTA */}
        <div className="mt-14 border-t border-white/10 pt-8 flex items-center justify-between flex-wrap gap-4">
          <div className="text-xs text-white/50 tracking-wider max-w-md">
            Play a run, earn chips, trade gems. The Trade Hall reopens every Monday at midnight UTC.
          </div>
          <Link
            to="/"
            data-testid="trade-highlights-cta"
            className="text-[10px] tracking-[0.4em] px-5 py-2 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors flex items-center gap-2"
          >
            ENTER THE PARKING LOT <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}
